import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';

import { REVIEW_STEP_DEFAULT_DEFINITION, STEP_STATE, WIZARD_PROGRESS } from '../../lib/constants';
import PHASES_CONFIG from '../../lib/phasesConfig';

import StatusBadge from './StatusBadge';
import SubstepRow from './SubstepRow';

const phaseMetaByCode = Object.fromEntries(PHASES_CONFIG.map((p) => [p.code, p]));

const PhaseHeader = ({ phase }) => {
  const meta = phaseMetaByCode[phase.code];
  const phaseIcon = meta?.icon;
  return (
    <li className="z-50 -mx-4 mt-3 flex h-12 items-center gap-2 bg-neutral-50 px-[22px] py-1 first:mt-0">
      {phaseIcon && <Icon icon={phaseIcon} size={36} />}
      <div className="flex flex-col">
        <span className="text-sm font-semibold leading-4 text-neutral-800">{phase.name}</span>
        {meta?.estimatedTime && (
          <span className="text-xs text-neutral-500">{meta.estimatedTime}</span>
        )}
      </div>
    </li>
  );
};

const StepSidebar = ({
  stepsState = [],
  phases = null,
  hiddenSubstepCodes = new Set(),
  onExpandStep,
  onCollapseStep,
  onActivateStep,
  onNavigateSubstep
}) => {
  const renderList = useMemo(() => {
    if (!phases) return stepsState.map((s) => ({ type: 'step', data: s }));
    const stateByCode = Object.fromEntries(stepsState.map((s) => [s.code, s]));

    const reviewStep = stateByCode[REVIEW_STEP_DEFAULT_DEFINITION.code];

    const phaseItems = phases.flatMap((phase, index) => {
      const phaseSteps = phase.steps
        .map((s) => stateByCode[s.code])
        .filter(Boolean)
        .sort((a, b) => a.order - b.order)
        .map((s) => ({ type: 'step', data: s }));

      const isLastPhase = index === phases.length - 1;
      const stepsWithVirtual = [
        ...phaseSteps,
        ...(isLastPhase && reviewStep ? [{ type: 'step', data: reviewStep }] : [])
      ];

      return [{ type: 'phase_header', data: phase }, ...stepsWithVirtual];
    });

    return phaseItems;
  }, [phases, stepsState]);

  const flatSteps = useMemo(
    () => renderList.filter((item) => item.type === 'step').map((item) => item.data),
    [renderList]
  );
  const activeStep = useMemo(
    () => flatSteps.find((step) => step.state_status === STEP_STATE.EXPANDED),
    [flatSteps]
  );

  const [isScrolled, setIsScrolled] = useState(false);
  const [animatedStepCode, setAnimatedStepCode] = useState(null);
  const scrollRef = useRef(null);
  const previousActiveStepCodeRef = useRef(null);
  const stepRefs = useRef({});

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      setIsScrolled(scrollRef.current.scrollTop > 0);
    }
  }, []);

  useEffect(() => {
    const activeStepCode = activeStep?.code;
    if (!activeStepCode) return;

    const previousActiveStepCode = previousActiveStepCodeRef.current;
    previousActiveStepCodeRef.current = activeStepCode;

    const activeStepElement = stepRefs.current[activeStepCode];
    const scrollContainer = scrollRef.current;
    if (activeStepElement && scrollContainer) {
      const elementTop = activeStepElement.offsetTop;
      const elementHeight = activeStepElement.offsetHeight;
      const containerHeight = scrollContainer.clientHeight;
      const maxScrollTop = scrollContainer.scrollHeight - containerHeight;
      const targetScrollTop = Math.min(
        Math.max(elementTop - (containerHeight / 2 - elementHeight / 2), 0),
        maxScrollTop
      );

      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      });
    }

    if (!previousActiveStepCode || previousActiveStepCode === activeStepCode) return;

    setAnimatedStepCode(activeStepCode);
    const timeoutId = setTimeout(() => {
      setAnimatedStepCode((currentCode) => (currentCode === activeStepCode ? null : currentCode));
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [activeStep?.code]);

  return (
    <div className="onboarding-header flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white">
      <div
        className={cn(
          'relative z-10 p-4 transition-shadow duration-200',
          isScrolled && 'shadow-[0_2px_8px_0_rgba(0,0,0,0.08)]'
        )}>
        <Icon icon="logo" />
      </div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="relative flex-1 overflow-y-auto overflow-x-hidden">
        <ul className="space-y-1 px-4 pb-4">
          {renderList.map((item, index) => {
            if (item.type === 'phase_header') {
              return <PhaseHeader key={`phase-${item.data.code}`} phase={item.data} />;
            }

            const step = item.data;
            const stepIndex = flatSteps.indexOf(step);
            const nextItem = renderList[index + 1];
            const isFirstStep = renderList[index - 1]?.type === 'phase_header';
            const isLastStep =
              stepIndex === flatSteps.length - 1 || nextItem?.type === 'phase_header';
            const isDone =
              step.status === WIZARD_PROGRESS.COMPLETED ||
              step.status === WIZARD_PROGRESS.CONFIRMED;

            return (
              <div
                ref={(element) => {
                  stepRefs.current[step.code] = element;
                }}
                className={cn(
                  'rounded-xl px-3 py-2 transition-all duration-300',
                  step.state_status === STEP_STATE.EXPANDED &&
                    'border border-solid border-primary-200 bg-primary-25',
                  step.state_status === STEP_STATE.EXPANDED &&
                    isDone &&
                    'border border-solid border-success-200 bg-success-25',
                  animatedStepCode === step.code && 'animate-pulse'
                )}
                key={step.step_id ?? step.id ?? step.code ?? index}>
                <li className="items-top relative flex cursor-pointer gap-3">
                  <StatusBadge
                    step={step}
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    onExpandStep={onExpandStep}
                    onCollapseStep={onCollapseStep}
                    onActivateStep={onActivateStep}
                  />
                </li>

                {step.state_status === STEP_STATE.EXPANDED &&
                  Array.isArray(step.substeps) &&
                  step.substeps.length > 1 && (
                    <ul
                      className="mt-5 space-y-6 border-l border-neutral-200 pl-0.5"
                      aria-label="Sub-items">
                      {step.substeps.map((sub, subIndex) => (
                        <SubstepRow
                          key={sub.substep_id ?? sub.id ?? sub.code ?? subIndex}
                          substep={sub}
                          isLastInTree={isLastStep && subIndex === step.substeps.length - 1}
                          onClick={() => onNavigateSubstep(sub.code)}
                          isHidden={hiddenSubstepCodes.has(sub.code)}
                        />
                      ))}
                    </ul>
                  )}
              </div>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default StepSidebar;
